import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message } from 'antd';
import { generateGroupNo } from '../../service/profile';
import './TeamModal.scss';

interface TeamModalProps {
  visible: boolean;
  type: 'create' | 'join' | 'exit';
  onCancel: () => void;
  onConfirm: (teamId?: string, teamName?: string) => void;
  loading?: boolean;
  currentTeamId?: string;
  auditorName?: string;
}

export const TeamModal: React.FC<TeamModalProps> = ({
  visible,
  type,
  onCancel,
  onConfirm,
  loading = false,
  currentTeamId = '',
  auditorName = ''
}) => {
  const [teamName, setTeamName] = useState('');
  const [teamId, setTeamId] = useState('');
  const [generatedGroupNo, setGeneratedGroupNo] = useState('');

  // 生成团队编号
  useEffect(() => {
    if (visible && type === 'create') {
      generateGroupNo()
        .then(groupNo => {
          setGeneratedGroupNo(groupNo);
        })
        .catch(error => {
          console.error('生成团队编号失败:', error);
          message.error('生成团队编号失败，请重试');
        });
    }
  }, [visible, type]);

  // 重置表单状态
  const handleCancel = () => {
    setTeamName('');
    setTeamId('');
    setGeneratedGroupNo('');
    onCancel();
  };

  // 处理确认操作
  const handleConfirm = () => {
    if (type === 'create') {
      if (!generatedGroupNo) {
        message.error('团队编号生成中，请稍候');
        return;
      }
      
      // 使用生成的团队编号，让父组件处理API调用
      onConfirm(generatedGroupNo, generatedGroupNo);
    } else if (type === 'join') {
      if (!teamId.trim()) {
        message.error('请输入团队名称');
        return;
      }
      onConfirm(teamId.trim());
    } else if (type === 'exit') {
      // 退出团队不需要额外验证
      onConfirm(currentTeamId);
    }
  };

  // 处理输入框回车事件
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  // 获取弹窗标题
  const getModalTitle = () => {
    switch (type) {
      case 'create':
        return '创建团队';
      case 'join':
        return '加入团队';
      case 'exit':
        return '退出团队';
      default:
        return '团队操作';
    }
  };

  // 渲染弹窗内容
  const renderModalContent = () => {
    switch (type) {
      case 'create':
        return (
          <div className="create-team-content">
            <div className="modal-text">团队编号</div>
            <Input
              value={generatedGroupNo}
              disabled
              placeholder="团队编号自动生成中..."
              className="team-name-input"
              autoFocus
            />
            {/* <div className="modal-hint">
              团队编号已自动生成，您将成为团队管理员
            </div> */}
          </div>
        );
      
      case 'join':
        return (
          <div className="join-team-content">
            <div className="modal-text">请输入团队名称</div>
            <Input
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="请输入团队名称"
              className="team-id-input"
              autoFocus
            />
            {/* <div className="modal-hint">
              输入正确的团队编号即可加入该团队
            </div> */}
          </div>
        );
      
      case 'exit':
        return (
          <div className="exit-team-content">
            <div className="exit-info-row">
              <span className="modal-text">您目前所在的团队为</span>
              <Input
                value={currentTeamId}
                readOnly
                className="team-id-display-inline"
                style={{ width: `${Math.max(currentTeamId.length * 12 + 16, 80)}px` }}
              />
            </div>
            <div className="exit-info-row">
              <span className="modal-text">审核者为</span>
              <Input
                value={auditorName}
                readOnly
                className="auditor-display-inline"
                style={{ width: `${Math.max(auditorName.length * 12 + 16, 80)}px` }}
              />
              <span className="modal-text">，是否确认退出?</span>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={loading}
        >
          确认
        </Button>
      ]}
      className="team-modal"
      width={400}
      centered
      destroyOnClose={true}
      maskClosable={false}
    >
      <div className="team-modal-content">
        {renderModalContent()}
      </div>
    </Modal>
  );
};
